import {
  HeatmapResponseSchema,
  MarketCandleSchema,
  MarketCandlesResponse,
  OrderBookEntrySchema,
  OrderBookMetricsResponseSchema,
  OrderBookResponseSchema,
  PriceImpactResponseSchema,
  RecentMarketTradeSchema,
  RecentMarketTradesResponse,
  TickerResponseSchema,
  VolumeProfileResponseSchema,
} from "@api/model/models";
import { extractApiPayload } from "./api-response.utils";
import { normalizeSymbolForApi } from "../../shared/utils/symbol.utils";

const DECIMAL = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const UNSIGNED_INTEGER = /^(?:0|[1-9]\d*)$/;

export class MarketDataContractError extends Error {
  readonly code = "market_data_contract_invalid";

  constructor(path: string, detail: string) {
    super(`[market_data_contract_invalid] ${path}: ${detail}`);
    this.name = "MarketDataContractError";
  }
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new MarketDataContractError(path, "expected an object");
  }
  return value as Record<string, unknown>;
}

function text(value: unknown, path: string): string {
  if (
    typeof value !== "string" ||
    value.trim() !== value ||
    value.length === 0
  ) {
    throw new MarketDataContractError(
      path,
      "expected a non-empty trimmed string",
    );
  }
  return value;
}

function optionalText(value: unknown, path: string): string | null | undefined {
  if (value === null || value === undefined) return value;
  return text(value, path);
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") {
    throw new MarketDataContractError(path, "expected a boolean");
  }
  return value;
}

function safeInteger(value: unknown, path: string, minimum = 0): number {
  if (!Number.isSafeInteger(value) || (value as number) < minimum) {
    throw new MarketDataContractError(
      path,
      `expected a safe integer greater than or equal to ${minimum}`,
    );
  }
  return value as number;
}

function decimal(
  value: unknown,
  path: string,
  sign: "any" | "nonnegative" | "positive" = "any",
): string {
  const parsed = text(value, path);
  if (!DECIMAL.test(parsed)) {
    throw new MarketDataContractError(path, "expected a plain decimal string");
  }
  const numeric = Number(parsed);
  if (!Number.isFinite(numeric)) {
    throw new MarketDataContractError(
      path,
      "decimal is outside the UI numeric domain",
    );
  }
  if (sign === "nonnegative" && numeric < 0) {
    throw new MarketDataContractError(path, "expected a nonnegative decimal");
  }
  if (sign === "positive" && numeric <= 0) {
    throw new MarketDataContractError(path, "expected a positive decimal");
  }
  return parsed;
}

function optionalDecimal(
  value: unknown,
  path: string,
  sign: "any" | "nonnegative" | "positive" = "any",
): string | null | undefined {
  if (value === null || value === undefined) return value;
  return decimal(value, path, sign);
}

function unsignedIntegerString(
  value: unknown,
  path: string,
  positive = false,
): string {
  const parsed = text(value, path);
  if (!UNSIGNED_INTEGER.test(parsed) || (positive && BigInt(parsed) === 0n)) {
    throw new MarketDataContractError(
      path,
      positive
        ? "expected a positive integer string"
        : "expected an unsigned integer string",
    );
  }
  return parsed;
}

function isoTimestamp(value: unknown, path: string): string {
  const parsed = text(value, path);
  if (
    !/^\d{4}-\d{2}-\d{2}T/.test(parsed) ||
    !Number.isFinite(Date.parse(parsed))
  ) {
    throw new MarketDataContractError(
      path,
      "expected a valid ISO-8601 timestamp",
    );
  }
  return parsed;
}

function sameSymbol(
  actual: string,
  expected: string | undefined,
  path: string,
): void {
  if (
    expected !== undefined &&
    normalizeSymbolForApi(actual) !== normalizeSymbolForApi(expected)
  ) {
    throw new MarketDataContractError(
      path,
      "response symbol does not match the request",
    );
  }
}

function optionalDecimalRange(
  value: unknown,
  path: string,
): { low: string; high: string } | null | undefined {
  if (value === null || value === undefined) return value;
  const range = record(value, path);
  const low = decimal(range["low"], `${path}.low`, "positive");
  const high = decimal(range["high"], `${path}.high`, "positive");
  if (Number(low) > Number(high)) {
    throw new MarketDataContractError(path, "low exceeds high");
  }
  return { low, high };
}

function decimalNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Convert a validated optional wire decimal for chart/display calculations. */
export function marketDecimalNumberOrNull(
  value: string | null | undefined,
): number | null {
  return decimalNumber(value);
}

export function parseTickerResponse(
  response: unknown,
  expectedSymbol?: string,
): TickerResponseSchema {
  const value = record(extractApiPayload<unknown>(response), "ticker");
  const symbol = text(value["symbol"], "ticker.symbol");
  sameSymbol(symbol, expectedSymbol, "ticker.symbol");
  const dataAvailable = bool(value["dataAvailable"], "ticker.dataAvailable");
  const exchange = optionalText(value["exchange"], "ticker.exchange");
  const lastPrice = optionalDecimal(
    value["lastPrice"],
    "ticker.lastPrice",
    "positive",
  );
  const bidPrice = optionalDecimal(
    value["bidPrice"],
    "ticker.bidPrice",
    "positive",
  );
  const bidQty = optionalDecimal(
    value["bidQty"],
    "ticker.bidQty",
    "nonnegative",
  );
  const askPrice = optionalDecimal(
    value["askPrice"],
    "ticker.askPrice",
    "positive",
  );
  const askQty = optionalDecimal(
    value["askQty"],
    "ticker.askQty",
    "nonnegative",
  );
  const spread = optionalDecimal(
    value["spread"],
    "ticker.spread",
    "nonnegative",
  );
  const change24h = optionalDecimal(value["change24h"], "ticker.change24h");
  const changePercent24h = optionalDecimal(
    value["changePercent24h"],
    "ticker.changePercent24h",
  );
  const high24h = optionalDecimal(
    value["high24h"],
    "ticker.high24h",
    "positive",
  );
  const low24h = optionalDecimal(value["low24h"], "ticker.low24h", "positive");
  const volume24h = optionalDecimal(
    value["volume24h"],
    "ticker.volume24h",
    "nonnegative",
  );
  const timestamp =
    value["timestamp"] === null || value["timestamp"] === undefined
      ? value["timestamp"]
      : isoTimestamp(value["timestamp"], "ticker.timestamp");
  const source = optionalText(value["source"], "ticker.source");
  const message = optionalText(value["message"], "ticker.message");

  if (dataAvailable) {
    if (exchange == null || timestamp == null || source == null) {
      throw new MarketDataContractError(
        "ticker",
        "available data requires exchange, source, and source timestamp",
      );
    }
    if (lastPrice == null && bidPrice == null && askPrice == null) {
      throw new MarketDataContractError(
        "ticker",
        "available data requires a last, bid, or ask price",
      );
    }
  } else {
    const measurements = [
      lastPrice,
      bidPrice,
      bidQty,
      askPrice,
      askQty,
      spread,
      change24h,
      changePercent24h,
      high24h,
      low24h,
      volume24h,
      timestamp,
    ];
    if (measurements.some((measurement) => measurement != null)) {
      throw new MarketDataContractError(
        "ticker",
        "unavailable data cannot carry measured values or freshness",
      );
    }
  }
  if (spread != null && (bidPrice == null || askPrice == null)) {
    throw new MarketDataContractError(
      "ticker.spread",
      "spread requires both bid and ask prices",
    );
  }
  if (
    bidPrice != null &&
    askPrice != null &&
    Number(bidPrice) > Number(askPrice)
  ) {
    throw new MarketDataContractError("ticker", "best bid exceeds best ask");
  }

  return {
    symbol,
    exchange,
    lastPrice,
    bidPrice,
    bidQty,
    askPrice,
    askQty,
    spread,
    change24h,
    changePercent24h,
    high24h,
    low24h,
    volume24h,
    timestamp,
    dataAvailable,
    source,
    message,
  };
}

function orderBookEntry(value: unknown, path: string): OrderBookEntrySchema {
  const level = record(value, path);
  return {
    price: decimal(level["price"], `${path}.price`, "positive"),
    quantity: decimal(level["quantity"], `${path}.quantity`, "positive"),
  };
}

export function parseOrderBookResponse(
  response: unknown,
  expectedSymbol?: string,
  expectedExchange?: string,
): OrderBookResponseSchema {
  const value = record(extractApiPayload<unknown>(response), "orderBook");
  const symbol = text(value["symbol"], "orderBook.symbol");
  sameSymbol(symbol, expectedSymbol, "orderBook.symbol");
  const exchange = text(value["exchange"], "orderBook.exchange");
  if (
    expectedExchange !== undefined &&
    exchange.toLowerCase() !== expectedExchange.trim().toLowerCase()
  ) {
    throw new MarketDataContractError(
      "orderBook.exchange",
      "response exchange does not match the request",
    );
  }
  if (!Array.isArray(value["bids"]) || !Array.isArray(value["asks"])) {
    throw new MarketDataContractError(
      "orderBook",
      "bids and asks must be arrays",
    );
  }
  const bids = value["bids"].map((level, index) =>
    orderBookEntry(level, `orderBook.bids[${index}]`),
  );
  const asks = value["asks"].map((level, index) =>
    orderBookEntry(level, `orderBook.asks[${index}]`),
  );
  if (bids.length === 0 && asks.length === 0) {
    throw new MarketDataContractError(
      "orderBook",
      "both sides cannot be empty",
    );
  }
  if (
    bids.some(
      (level, index) =>
        index > 0 && Number(bids[index - 1].price) < Number(level.price),
    )
  ) {
    throw new MarketDataContractError(
      "orderBook.bids",
      "levels are not descending",
    );
  }
  if (
    asks.some(
      (level, index) =>
        index > 0 && Number(asks[index - 1].price) > Number(level.price),
    )
  ) {
    throw new MarketDataContractError(
      "orderBook.asks",
      "levels are not ascending",
    );
  }
  if (
    bids.length > 0 &&
    asks.length > 0 &&
    Number(bids[0].price) > Number(asks[0].price)
  ) {
    throw new MarketDataContractError("orderBook", "best bid exceeds best ask");
  }
  const sequence =
    value["sequence"] === null || value["sequence"] === undefined
      ? value["sequence"]
      : safeInteger(value["sequence"], "orderBook.sequence");
  const synthetic =
    value["synthetic"] === null || value["synthetic"] === undefined
      ? value["synthetic"]
      : bool(value["synthetic"], "orderBook.synthetic");
  if (synthetic === true) {
    throw new MarketDataContractError(
      "orderBook.synthetic",
      "synthetic books are not accepted",
    );
  }
  return {
    symbol,
    bids,
    asks,
    exchange,
    timestamp: isoTimestamp(value["timestamp"], "orderBook.timestamp"),
    sequence,
    synthetic,
    message: optionalText(value["message"], "orderBook.message"),
  };
}

export function parseOrderBookMetricsResponse(
  response: unknown,
  expectedSymbol?: string,
): OrderBookMetricsResponseSchema {
  const value = record(response, "orderBookMetrics");
  const symbol = text(value["symbol"], "orderBookMetrics.symbol");
  sameSymbol(symbol, expectedSymbol, "orderBookMetrics.symbol");
  const metrics = record(value["metrics"], "orderBookMetrics.metrics");
  const bidDepthLevels = safeInteger(
    metrics["bidDepthLevels"],
    "orderBookMetrics.metrics.bidDepthLevels",
  );
  const askDepthLevels = safeInteger(
    metrics["askDepthLevels"],
    "orderBookMetrics.metrics.askDepthLevels",
  );
  const bidLiquidity = optionalDecimal(
    metrics["bidLiquidity"],
    "orderBookMetrics.metrics.bidLiquidity",
    "nonnegative",
  );
  const askLiquidity = optionalDecimal(
    metrics["askLiquidity"],
    "orderBookMetrics.metrics.askLiquidity",
    "nonnegative",
  );
  const totalLiquidity = optionalDecimal(
    metrics["totalLiquidity"],
    "orderBookMetrics.metrics.totalLiquidity",
    "nonnegative",
  );
  const depthRatio = optionalDecimal(
    metrics["depthRatio"],
    "orderBookMetrics.metrics.depthRatio",
    "nonnegative",
  );
  if (depthRatio != null && Number(depthRatio) > 1) {
    throw new MarketDataContractError(
      "orderBookMetrics.metrics.depthRatio",
      "ratio exceeds one",
    );
  }
  if (
    (bidDepthLevels > 0 &&
      (bidLiquidity == null || Number(bidLiquidity) === 0)) ||
    (bidDepthLevels === 0 && bidLiquidity != null && Number(bidLiquidity) !== 0)
  ) {
    throw new MarketDataContractError(
      "orderBookMetrics.metrics.bidLiquidity",
      "liquidity must agree with the measured bid depth",
    );
  }
  if (
    (askDepthLevels > 0 &&
      (askLiquidity == null || Number(askLiquidity) === 0)) ||
    (askDepthLevels === 0 && askLiquidity != null && Number(askLiquidity) !== 0)
  ) {
    throw new MarketDataContractError(
      "orderBookMetrics.metrics.askLiquidity",
      "liquidity must agree with the measured ask depth",
    );
  }
  if (
    totalLiquidity == null &&
    (bidLiquidity != null || askLiquidity != null)
  ) {
    throw new MarketDataContractError(
      "orderBookMetrics.metrics.totalLiquidity",
      "side liquidity requires measured total liquidity",
    );
  }

  return {
    symbol,
    exchange: text(value["exchange"], "orderBookMetrics.exchange"),
    timestamp: isoTimestamp(value["timestamp"], "orderBookMetrics.timestamp"),
    depthAmount: decimal(
      value["depthAmount"],
      "orderBookMetrics.depthAmount",
      "positive",
    ),
    metrics: {
      bidLiquidity,
      askLiquidity,
      totalLiquidity,
      bidDepthLevels,
      askDepthLevels,
      depthRatio,
      totalBidVolume: optionalDecimal(
        metrics["totalBidVolume"],
        "orderBookMetrics.metrics.totalBidVolume",
        "nonnegative",
      ),
      totalAskVolume: optionalDecimal(
        metrics["totalAskVolume"],
        "orderBookMetrics.metrics.totalAskVolume",
        "nonnegative",
      ),
      spread: optionalDecimal(
        metrics["spread"],
        "orderBookMetrics.metrics.spread",
        "nonnegative",
      ),
      midPrice: optionalDecimal(
        metrics["midPrice"],
        "orderBookMetrics.metrics.midPrice",
        "positive",
      ),
      imbalanceRatio: (() => {
        const imbalance = optionalDecimal(
          metrics["imbalanceRatio"],
          "orderBookMetrics.metrics.imbalanceRatio",
        );
        if (imbalance != null && Math.abs(Number(imbalance)) > 1) {
          throw new MarketDataContractError(
            "orderBookMetrics.metrics.imbalanceRatio",
            "ratio must be between minus one and one",
          );
        }
        return imbalance;
      })(),
    },
    source: text(value["source"], "orderBookMetrics.source"),
  };
}

function trade(value: unknown, path: string): RecentMarketTradeSchema {
  const item = record(value, path);
  const symbol = record(item["symbol"], `${path}.symbol`);
  const fees =
    item["fees"] == null
      ? item["fees"]
      : (() => {
          const fee = record(item["fees"], `${path}.fees`);
          return {
            amount: decimal(
              fee["amount"],
              `${path}.fees.amount`,
              "nonnegative",
            ),
            currency: text(fee["currency"], `${path}.fees.currency`),
            fee_type: text(fee["fee_type"], `${path}.fees.fee_type`),
          };
        })();
  const side = text(item["side"], `${path}.side`).toLowerCase();
  if (side !== "buy" && side !== "sell") {
    throw new MarketDataContractError(`${path}.side`, "expected buy or sell");
  }
  return {
    id: text(item["id"], `${path}.id`),
    symbol: {
      base: text(symbol["base"], `${path}.symbol.base`),
      quote: text(symbol["quote"], `${path}.symbol.quote`),
    },
    exchange: text(item["exchange"], `${path}.exchange`),
    price: decimal(item["price"], `${path}.price`, "positive"),
    volume: decimal(item["volume"], `${path}.volume`, "positive"),
    side,
    timestamp: unsignedIntegerString(
      item["timestamp"],
      `${path}.timestamp`,
      true,
    ),
    fees,
  };
}

export function parseRecentMarketTradesResponse(
  response: unknown,
  expectedSymbol?: string,
): RecentMarketTradesResponse {
  const value = record(response, "recentTrades");
  if (text(value["status"], "recentTrades.status") !== "success") {
    throw new MarketDataContractError(
      "recentTrades.status",
      "expected success",
    );
  }
  const data = record(value["data"], "recentTrades.data");
  const symbol = text(data["symbol"], "recentTrades.data.symbol");
  sameSymbol(symbol, expectedSymbol, "recentTrades.data.symbol");
  if (!Array.isArray(data["trades"])) {
    throw new MarketDataContractError(
      "recentTrades.data.trades",
      "expected an array",
    );
  }
  const trades = data["trades"].map((item, index) =>
    trade(item, `recentTrades.data.trades[${index}]`),
  );
  const count = safeInteger(data["count"], "recentTrades.data.count");
  if (count !== trades.length) {
    throw new MarketDataContractError(
      "recentTrades.data.count",
      "does not match trades length",
    );
  }
  return {
    status: "success",
    data: { trades, count, symbol },
    timestamp: isoTimestamp(value["timestamp"], "recentTrades.timestamp"),
    request_id: text(value["request_id"], "recentTrades.request_id"),
  };
}

function candle(value: unknown, path: string): MarketCandleSchema {
  const item = record(value, path);
  const symbol = record(item["symbol"], `${path}.symbol`);
  const startTime = isoTimestamp(item["start_time"], `${path}.start_time`);
  const endTime = isoTimestamp(item["end_time"], `${path}.end_time`);
  if (Date.parse(startTime) >= Date.parse(endTime)) {
    throw new MarketDataContractError(path, "candle start must precede end");
  }
  const open = decimal(item["open"], `${path}.open`, "positive");
  const high = decimal(item["high"], `${path}.high`, "positive");
  const low = decimal(item["low"], `${path}.low`, "positive");
  const close = decimal(item["close"], `${path}.close`, "positive");
  if (
    Number(high) < Math.max(Number(open), Number(close)) ||
    Number(low) > Math.min(Number(open), Number(close)) ||
    Number(low) > Number(high)
  ) {
    throw new MarketDataContractError(path, "OHLC bounds are inconsistent");
  }
  return {
    symbol: {
      base: text(symbol["base"], `${path}.symbol.base`),
      quote: text(symbol["quote"], `${path}.symbol.quote`),
      symbol: text(symbol["symbol"], `${path}.symbol.symbol`),
      pair: text(symbol["pair"], `${path}.symbol.pair`),
    },
    exchange: text(item["exchange"], `${path}.exchange`),
    open,
    high,
    low,
    close,
    volume: decimal(item["volume"], `${path}.volume`, "nonnegative"),
    quote_volume: decimal(
      item["quote_volume"],
      `${path}.quote_volume`,
      "nonnegative",
    ),
    trade_count: unsignedIntegerString(
      item["trade_count"],
      `${path}.trade_count`,
    ),
    timestamp: unsignedIntegerString(
      item["timestamp"],
      `${path}.timestamp`,
      true,
    ),
    start_time: startTime,
    end_time: endTime,
    period_nanos: unsignedIntegerString(
      item["period_nanos"],
      `${path}.period_nanos`,
      true,
    ),
  };
}

export function parseMarketCandlesResponse(
  response: unknown,
  expectedSymbol?: string,
  expectedInterval?: string,
): MarketCandlesResponse {
  const value = record(response, "candles");
  if (text(value["status"], "candles.status") !== "success") {
    throw new MarketDataContractError("candles.status", "expected success");
  }
  const data = record(value["data"], "candles.data");
  const symbol = text(data["symbol"], "candles.data.symbol");
  sameSymbol(symbol, expectedSymbol, "candles.data.symbol");
  const interval = text(data["interval"], "candles.data.interval");
  if (expectedInterval !== undefined && interval !== expectedInterval) {
    throw new MarketDataContractError(
      "candles.data.interval",
      "response interval does not match request",
    );
  }
  if (!Array.isArray(data["candles"])) {
    throw new MarketDataContractError(
      "candles.data.candles",
      "expected an array",
    );
  }
  const candles = data["candles"].map((item, index) =>
    candle(item, `candles.data.candles[${index}]`),
  );
  const count = safeInteger(data["count"], "candles.data.count");
  if (count !== candles.length) {
    throw new MarketDataContractError(
      "candles.data.count",
      "does not match candles length",
    );
  }
  return {
    status: "success",
    data: {
      candles,
      count,
      symbol,
      interval,
      start_time: isoTimestamp(data["start_time"], "candles.data.start_time"),
      end_time: isoTimestamp(data["end_time"], "candles.data.end_time"),
    },
  };
}

export function parseHeatmapResponse(
  response: unknown,
  expectedSymbol?: string,
): HeatmapResponseSchema {
  const value = record(response, "heatmap");
  const symbol = text(value["symbol"], "heatmap.symbol");
  sameSymbol(symbol, expectedSymbol, "heatmap.symbol");
  if (!Array.isArray(value["heatmapData"])) {
    throw new MarketDataContractError(
      "heatmap.heatmapData",
      "expected an array",
    );
  }
  const heatmapData = value["heatmapData"].map((point, pointIndex) => {
    const item = record(point, `heatmap.heatmapData[${pointIndex}]`);
    const parseLevels = (side: "bidLevels" | "askLevels") => {
      if (!Array.isArray(item[side])) {
        throw new MarketDataContractError(
          `heatmap.${side}`,
          "expected an array",
        );
      }
      return item[side].map((level, levelIndex) => {
        const row = record(level, `heatmap.${side}[${levelIndex}]`);
        return {
          price: decimal(
            row["price"],
            `heatmap.${side}[${levelIndex}].price`,
            "positive",
          ),
          quantity: decimal(
            row["quantity"],
            `heatmap.${side}[${levelIndex}].quantity`,
            "positive",
          ),
          intensity: decimal(
            row["intensity"],
            `heatmap.${side}[${levelIndex}].intensity`,
            "nonnegative",
          ),
        };
      });
    };
    return {
      timestamp: isoTimestamp(
        item["timestamp"],
        `heatmap.heatmapData[${pointIndex}].timestamp`,
      ),
      midPrice: optionalDecimal(
        item["midPrice"],
        `heatmap.heatmapData[${pointIndex}].midPrice`,
        "positive",
      ),
      spread: optionalDecimal(
        item["spread"],
        `heatmap.heatmapData[${pointIndex}].spread`,
        "nonnegative",
      ),
      bidLevels: parseLevels("bidLevels"),
      askLevels: parseLevels("askLevels"),
    };
  });
  const snapshotCount = safeInteger(
    value["snapshotCount"],
    "heatmap.snapshotCount",
  );
  const historicalDataAvailable = bool(
    value["historicalDataAvailable"],
    "heatmap.historicalDataAvailable",
  );
  if (snapshotCount !== heatmapData.length) {
    throw new MarketDataContractError(
      "heatmap.snapshotCount",
      "does not match heatmapData length",
    );
  }
  if (historicalDataAvailable !== snapshotCount > 0) {
    throw new MarketDataContractError(
      "heatmap",
      "availability does not match snapshot count",
    );
  }
  const priceRange = optionalDecimalRange(
    value["priceRange"],
    "heatmap.priceRange",
  );
  if (historicalDataAvailable && priceRange == null) {
    throw new MarketDataContractError(
      "heatmap.priceRange",
      "measured heatmap requires a range",
    );
  }
  const periodStart = isoTimestamp(value["periodStart"], "heatmap.periodStart");
  const periodEnd = isoTimestamp(value["periodEnd"], "heatmap.periodEnd");
  if (Date.parse(periodStart) > Date.parse(periodEnd)) {
    throw new MarketDataContractError(
      "heatmap",
      "period start exceeds period end",
    );
  }
  return {
    symbol,
    exchange: optionalText(value["exchange"], "heatmap.exchange"),
    windowMinutes: safeInteger(
      value["windowMinutes"],
      "heatmap.windowMinutes",
      1,
    ),
    levels: safeInteger(value["levels"], "heatmap.levels", 1),
    priceRange,
    periodStart,
    periodEnd,
    maxQuantity: decimal(
      value["maxQuantity"],
      "heatmap.maxQuantity",
      "nonnegative",
    ),
    snapshotCount,
    heatmapData,
    historicalDataAvailable,
    message: optionalText(value["message"], "heatmap.message"),
  };
}

export function parseVolumeProfileResponse(
  response: unknown,
  expectedSymbol?: string,
): VolumeProfileResponseSchema {
  const value = record(response, "volumeProfile");
  const symbol = text(value["symbol"], "volumeProfile.symbol");
  sameSymbol(symbol, expectedSymbol, "volumeProfile.symbol");
  if (!Array.isArray(value["volumeBuckets"])) {
    throw new MarketDataContractError(
      "volumeProfile.volumeBuckets",
      "expected an array",
    );
  }
  const volumeBuckets = value["volumeBuckets"].map((bucket, index) => {
    const row = record(bucket, `volumeProfile.volumeBuckets[${index}]`);
    return {
      price: decimal(
        row["price"],
        `volumeProfile.volumeBuckets[${index}].price`,
        "positive",
      ),
      volume: decimal(
        row["volume"],
        `volumeProfile.volumeBuckets[${index}].volume`,
        "nonnegative",
      ),
    };
  });
  const dataAvailable = bool(
    value["dataAvailable"],
    "volumeProfile.dataAvailable",
  );
  if (dataAvailable !== volumeBuckets.length > 0) {
    throw new MarketDataContractError(
      "volumeProfile",
      "availability does not match buckets",
    );
  }
  const periodStart =
    value["periodStart"] == null
      ? value["periodStart"]
      : isoTimestamp(value["periodStart"], "volumeProfile.periodStart");
  const periodEnd =
    value["periodEnd"] == null
      ? value["periodEnd"]
      : isoTimestamp(value["periodEnd"], "volumeProfile.periodEnd");
  if (
    periodStart != null &&
    periodEnd != null &&
    Date.parse(periodStart) > Date.parse(periodEnd)
  ) {
    throw new MarketDataContractError(
      "volumeProfile",
      "period start exceeds period end",
    );
  }
  const priceRange = optionalDecimalRange(
    value["priceRange"],
    "volumeProfile.priceRange",
  );
  const valueArea = optionalDecimalRange(
    value["valueArea"],
    "volumeProfile.valueArea",
  );
  const poc =
    value["poc"] == null
      ? value["poc"]
      : (() => {
          const item = record(value["poc"], "volumeProfile.poc");
          return {
            price: optionalDecimal(
              item["price"],
              "volumeProfile.poc.price",
              "positive",
            ),
            volume: optionalDecimal(
              item["volume"],
              "volumeProfile.poc.volume",
              "nonnegative",
            ),
          };
        })();
  const bucketSize = optionalDecimal(
    value["bucketSize"],
    "volumeProfile.bucketSize",
    "positive",
  );
  const totalVolume = optionalDecimal(
    value["totalVolume"],
    "volumeProfile.totalVolume",
    "nonnegative",
  );
  const totalTrades =
    value["totalTrades"] == null
      ? value["totalTrades"]
      : safeInteger(value["totalTrades"], "volumeProfile.totalTrades");
  const source = optionalText(value["source"], "volumeProfile.source");
  if (
    dataAvailable &&
    (periodStart == null ||
      periodEnd == null ||
      priceRange == null ||
      bucketSize == null ||
      totalVolume == null ||
      totalTrades == null ||
      source == null)
  ) {
    throw new MarketDataContractError(
      "volumeProfile",
      "available profile requires its measured period, range, totals, bucket size, and source",
    );
  }
  if (
    !dataAvailable &&
    [
      periodStart,
      periodEnd,
      priceRange,
      bucketSize,
      poc,
      valueArea,
      totalVolume,
      totalTrades,
      source,
    ].some((measurement) => measurement != null)
  ) {
    throw new MarketDataContractError(
      "volumeProfile",
      "unavailable profile cannot carry measured values or freshness",
    );
  }
  return {
    symbol,
    exchange: optionalText(value["exchange"], "volumeProfile.exchange"),
    period: text(value["period"], "volumeProfile.period"),
    periodStart,
    periodEnd,
    priceRange,
    bucketSize,
    volumeBuckets,
    poc,
    valueArea,
    totalVolume,
    totalTrades,
    source,
    dataAvailable,
    message: optionalText(value["message"], "volumeProfile.message"),
  };
}

export function parsePriceImpactResponse(
  response: unknown,
  expectedSymbol?: string,
  expectedSide?: "buy" | "sell",
  expectedSize?: number,
): PriceImpactResponseSchema {
  const value = record(response, "priceImpact");
  const symbol = text(value["symbol"], "priceImpact.symbol");
  sameSymbol(symbol, expectedSymbol, "priceImpact.symbol");
  const execution = record(value["execution"], "priceImpact.execution");
  const impact = record(value["impact"], "priceImpact.impact");
  const market = record(
    value["marketCondition"],
    "priceImpact.marketCondition",
  );
  const side = text(value["side"], "priceImpact.side").toLowerCase();
  if (side !== "buy" && side !== "sell") {
    throw new MarketDataContractError(
      "priceImpact.side",
      "expected buy or sell",
    );
  }
  if (expectedSide !== undefined && side !== expectedSide) {
    throw new MarketDataContractError(
      "priceImpact.side",
      "response side does not match request",
    );
  }
  const requestedSize = decimal(
    value["requestedSize"],
    "priceImpact.requestedSize",
    "positive",
  );
  const filledSize = decimal(
    value["filledSize"],
    "priceImpact.filledSize",
    "nonnegative",
  );
  const unfilledSize = decimal(
    value["unfilledSize"],
    "priceImpact.unfilledSize",
    "nonnegative",
  );
  const requestedNumeric = Number(requestedSize);
  const measuredTotal = Number(filledSize) + Number(unfilledSize);
  const tolerance =
    Number.EPSILON * Math.max(1, requestedNumeric, measuredTotal) * 4;
  if (Math.abs(requestedNumeric - measuredTotal) > tolerance) {
    throw new MarketDataContractError(
      "priceImpact",
      "filled and unfilled size do not equal the requested size",
    );
  }
  if (
    expectedSize !== undefined &&
    (!Number.isFinite(expectedSize) ||
      expectedSize <= 0 ||
      Math.abs(requestedNumeric - expectedSize) >
        Number.EPSILON * Math.max(1, requestedNumeric, expectedSize) * 4)
  ) {
    throw new MarketDataContractError(
      "priceImpact.requestedSize",
      "response size does not match request",
    );
  }
  const bestBid = optionalDecimal(
    market["bestBid"],
    "priceImpact.marketCondition.bestBid",
    "positive",
  );
  const bestAsk = optionalDecimal(
    market["bestAsk"],
    "priceImpact.marketCondition.bestAsk",
    "positive",
  );
  if (bestBid != null && bestAsk != null && Number(bestBid) > Number(bestAsk)) {
    throw new MarketDataContractError(
      "priceImpact.marketCondition",
      "best bid exceeds best ask",
    );
  }
  return {
    symbol,
    side,
    requestedSize,
    filledSize,
    unfilledSize,
    execution: {
      avgPrice: decimal(
        execution["avgPrice"],
        "priceImpact.execution.avgPrice",
        "positive",
      ),
      totalCost: decimal(
        execution["totalCost"],
        "priceImpact.execution.totalCost",
        "nonnegative",
      ),
      levelsConsumed: safeInteger(
        execution["levelsConsumed"],
        "priceImpact.execution.levelsConsumed",
      ),
    },
    impact: {
      referencePrice: decimal(
        impact["referencePrice"],
        "priceImpact.impact.referencePrice",
        "positive",
      ),
      midPrice: decimal(
        impact["midPrice"],
        "priceImpact.impact.midPrice",
        "positive",
      ),
      absoluteImpact: decimal(
        impact["absoluteImpact"],
        "priceImpact.impact.absoluteImpact",
        "nonnegative",
      ),
      impactBps: decimal(
        impact["impactBps"],
        "priceImpact.impact.impactBps",
        "nonnegative",
      ),
      slippagePercent: decimal(
        impact["slippagePercent"],
        "priceImpact.impact.slippagePercent",
        "nonnegative",
      ),
    },
    marketCondition: {
      bestBid,
      bestAsk,
      availableLiquidity: safeInteger(
        market["availableLiquidity"],
        "priceImpact.marketCondition.availableLiquidity",
        1,
      ),
    },
    source: text(value["source"], "priceImpact.source"),
    warning: optionalText(value["warning"], "priceImpact.warning"),
  };
}
